// Re-entry into the first-run funnel (ENG-3796).
//
// router.tsx mounts OnboardingGate outermost, so Studio Settings renders INSIDE
// the gate's children. The button and the funnel therefore share no React state
// to lift, and something has to cross that gap. A zustand store is how this
// codebase already crosses it — store/popup.ts, auth/store.ts,
// routes/electron/tray/store.ts and seven more — so per
// `investigate-before-adding.md` this follows that convention rather than the
// three stringly-typed window CustomEvents that also exist here.
//
// What a restart deliberately does NOT do: clear the server's onboarded flag.
// The server derives `onboarded` from "has ever added a camera OR explicitly
// finished this screen", and once a camera exists that answer is permanent and
// correct. Asking to walk the funnel again is not asking to be asked on every
// future launch. So a restart reopens the funnel for THIS launch and writes
// nothing — see gate.tsx for the reset it performs.
//
// Imported directly rather than through onboarding/index.js on purpose: index
// re-exports the gate, and the gate's import graph reaches a Vite `?worker`
// module jest cannot link. A caller that reached the store through index would
// drag that graph into its own test. This file is a leaf and stays one.
//
// Per rules: code-comments-required, reference-rules-in-comments
// (ai.knowledge/global).

import { create } from 'zustand';

interface Store {
  /**
   * Set the moment Settings asks, cleared by the gate once it has reopened.
   *
   * A request rather than a piece of funnel state: only the gate can honour it,
   * and only the gate knows what reopening means. Clearing it there is what
   * lets a second click restart the funnel again.
   */
  readonly restartRequested: boolean;
  requestRestart: () => void;
  clearRestart: () => void;
}

export const useOnboardingRestart = create<Store>()((set) => ({
  restartRequested: false,
  requestRestart: () => set({ restartRequested: true }),
  clearRestart: () => set({ restartRequested: false })
}));

/**
 * Who is allowed to ask for OS permissions right now (ENG-3796).
 *
 * Two components can open a permissions dialog: the funnel's own step, and the
 * standalone OnboardPermissionsModal that `room.tsx` mounts for every Electron
 * user. They are both modals on `/:roomId`, so the funnel's route-based
 * `funnelDefersToRoute` cannot separate them — and when both opened, the
 * standalone one rendered UNDERNEATH the funnel where it could not be clicked,
 * leaving the user unable to grant the permissions the funnel was about to need.
 *
 * So the funnel CLAIMS the ask while it is running, and the standalone modal
 * stands down for exactly that long. It is a claim rather than a permanent
 * disable because a user who dismisses or skips the funnel still needs the
 * ask — the gate releases on unmount, and the standalone modal takes over.
 *
 * A store rather than the `window.irisPermissionModalShown` global already in
 * permissions.tsx: that global is write-once and never cleared, so borrowing it
 * would suppress the modal for the rest of the session even for a user who
 * skipped the funnel on its first screen.
 *
 * Per rules: think-in-systems, reference-rules-in-comments (ai.knowledge/global).
 */
interface PermissionsClaimStore {
  /** True while the funnel is on screen and owns the permissions ask. */
  readonly funnelOwnsPermissions: boolean;
  claimPermissions: () => void;
  releasePermissions: () => void;
}

export const useOnboardingPermissionsClaim = create<PermissionsClaimStore>()((set) => ({
  funnelOwnsPermissions: false,
  claimPermissions: () => set({ funnelOwnsPermissions: true }),
  releasePermissions: () => set({ funnelOwnsPermissions: false })
}));
